import React from 'react';
import {
  Link,
  Typography,
  styled,
} from '@material-ui/core';
import { cellTypes, headerTypes } from '../../../bento-core';
import DocumentDownload from '../../DocumentDownload/DocumentDownloadView';
import { hasMultiStudyParticipants } from '../../../utils/columnsUtil';
import MultiStudyTooltip from './components/multiStudyTooltip';
import { customizeColumn, customizeHeader, customizeLandScapeView } from './Types';
import DataAvailabilityCellView from './DataAvailability/TableCell';
import DataAvailabilityHeader from './DataAvailability/HeaderCell';
import NumberOfCasesView from './components/NumberOfCases';
import StudyLink from './components/StudyLink';
import CustomHeaderRemover from './components/CustomHeaderRemover';
import DeleteButton from './components/DeleteButton';
import DataValue from './components/DataValue';
import CsvDownload from './components/CsvDownload';
import { defaultTo } from 'lodash';

const ClinicalDataNodeWrapper = styled('span')(({ $hasNoValues }) => ({
  fontFamily: 'Nunito',
  fontSize: '16px',
  fontWeight: '700',
  lineHeight: '16px',
  letterSpacing: '0em',
  ...($hasNoValues ? { color: '#AAAAAA' } : { color: '#027DA7' }),
}));

const ClinicalDataDescriptionWrapper = styled('span')(({ $hasNoValues }) => ({
  fontFamily: 'Nunito',
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '20px',
  letterSpacing: '0em',
  ...($hasNoValues ? { color: '#AAAAAA' } : { color: '#13344A' }),
}));

const ClinicalDataCountWrapper = styled('span')(({ $hasNoValues }) => ({
  fontFamily: 'Nunito',
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '16px',
  letterSpacing: '0em',
  ...($hasNoValues ? { color: '#AAAAAA' } : { color: '#13344A' }),
}));

const CaseIdLink = (props) => {
  const {
    other_cases: otherCases,
    linkAttr = {},
    case_id: caseId,
    unifiedView,
  } = props;
  const { rootPath, pathParams } = linkAttr;
  const url = pathParams.map((attr) => `#${rootPath}/`.concat(props[attr]));
  return (
    <div style={{ display: 'flex' }}>
      <Link href={url} className={cellTypes.LINK}>
        <Typography>
          {caseId}
        </Typography>
      </Link>
      {
        ((!unifiedView) && hasMultiStudyParticipants(otherCases))
        && (
          <>
            <MultiStudyTooltip
              tableMeta={otherCases}
              value={caseId}
            />
          </>
        )
      }
    </div>
  );
};

const ArmsCohortList = (props) => {
  const { dataField } = props;
  const value = props[dataField];
  return value
    .split('#').map((desc) => (desc === '' ? ''
      : <li style={{ listStyleType: 'none' }}>{desc}</li>));
};

const ArmView = (props) => {
  const { groupIndex } = props;
  return (
    <>
      {
        (groupIndex === 0)
          && (<span> {props.arm} </span>)
      }
    </>
  )
}

const ArmDescView = (props) => {
  const { groupIndex } = props;
  return (
    <>
      {
        (groupIndex === 0)
          && (<span> {props.armDescription} </span>)
      }
    </>
  )
}

const DocumentDownloadView = ({
  file_size: fileSize,
  file_format: fileFormat,
  file_name: caseId,
  file_uuid: fileLocation,
  documentDownloadProps,
  ...props
}) => (
  <DocumentDownload
    fileSize={fileSize}
    fileFormat={fileFormat}
    caseId={caseId}
    studyAccession={props[documentDownloadProps.studyAccessionColumn] || props.study_accession}
    {...documentDownloadProps}
    fileLocation={props[documentDownloadProps.fileLocation]
      ? props[documentDownloadProps.fileLocation]
      : fileLocation}
  />
);

// Custom Cell View
export const CustomCellView = (props) => {
  const { dataField, clinicalDataDescription, clinicalDataNode, participantCount, csvDataRow } = props;
  const hasNoValues = participantCount === 0 && defaultTo(csvDataRow, []).length === 0;

  switch (dataField) {
    case customizeColumn.DOCUMENT_DOWNLOAD:
      return (
        <DocumentDownloadView {...props} />
      );
    case customizeColumn.MULTI_STUDY_PARTICIPATION:
      return (
        <CaseIdLink {...props} />
      );
    case customizeColumn.numberOfCases:
      return (
        <NumberOfCasesView {...props} />
      );
    case customizeColumn.STUDY_CODE:
      return (
        <StudyLink {...props} />
      );
    case customizeColumn.Description:
    case customizeColumn.Arm:
      return (
        <ArmView {...props} />
      );
    case customizeColumn.ArmDescription:
      return (
        <ArmDescView {...props} />
      );
    case customizeColumn.Cohort:
      return (
        <ArmsCohortList {...props} />
      );
      ///
    case customizeLandScapeView.PARTICIPANT_FILES: 
    case customizeLandScapeView.STUDY_FILES:
    case customizeLandScapeView.IMAGE_COLLECTION_COUNT:
    case customizeLandScapeView.PUBLICATIONS:
    case customizeLandScapeView.ADDITIONAL_CRDC_NODES:
      return (
        <DataAvailabilityCellView {...props} />
      );
    case customizeColumn.DataValue:
      return (
        <DataValue {...props} />
      );
    case customizeColumn.csvDataRow:
      return (
        <CsvDownload {...props} />
      );
    case customizeColumn.clinicalDataNode: 
      return <ClinicalDataNodeWrapper $hasNoValues={hasNoValues}>{clinicalDataNode}</ClinicalDataNodeWrapper>
    case customizeColumn.clinicalDataDescription:
      return <ClinicalDataDescriptionWrapper $hasNoValues={hasNoValues}>{clinicalDataDescription}</ClinicalDataDescriptionWrapper>
    case customizeColumn.participantCount:
      return <ClinicalDataCountWrapper $hasNoValues={hasNoValues}>{participantCount}</ClinicalDataCountWrapper>
    case customizeColumn.recordCount:
      return <ClinicalDataCountWrapper $hasNoValues={hasNoValues}>{props.recordCount}</ClinicalDataCountWrapper>
    default:
      return (<></>);
  }
};

// Custom Header View
export const CustomHeaderCellView = (props) => {
  const {
    dataField,
    icon,
    iconAlt,
    openDialogBox,
    cellType,
  } = props;
  switch (dataField || cellType) {
    case customizeHeader.PARTICIPANT_FILES:
    case customizeHeader.STUDY_FILES:
    case customizeHeader.IMAGE_COLLECTION_COUNT:
    case customizeHeader.PUBLICATIONS:
    case customizeHeader.ADDITIONAL_CRDC_NODES:
      return (
        <DataAvailabilityHeader icon={icon} iconAlt={iconAlt} dataField={dataField} />
      );
    case customizeHeader.DELETE:
      return (
        <CustomHeaderRemover openDialogBox={openDialogBox} />
      );
    default:
      return (<></>);
  }
};

/**
* set column configuration
* @param {*} columns
* @returns config columns
*/
export const CustomizeCellView = ({
  columns,
  unifiedView = false,
  interOpData,
  deleteCartFile,
  deleteAllFiles,
}) => {
  /**
  * display columns as configuration
  * set custom cell render for column
  */
  // const displayColumns = columns.filter((col) => col.display);
  const displayCustomView = [...columns].map((column) => {
    if (column.cellType === cellTypes.CUSTOM_ELEM) {
      return {
        ...column,
        customCellRender: (props) => (
          <CustomCellView
            {...props}
            unifiedView={unifiedView}
            interOpData={interOpData}
          />
        ),
      };
    }
    if (column.cellType === cellTypes.DELETE) {
      return {
        ...column,
        customCellRender: (row, onDeleteRow) => (
          <>
            <DeleteButton
              row={row}
              onDeleteRow={onDeleteRow}
              deleteCartFile={deleteCartFile}
            />
          </>
        ),
      };
    }
    return column;
  });

  /**
  * custom header view configuration
  */
  const displayCustomHeader = [...displayCustomView].map((column) => {
    if (column.headerType === headerTypes.CUSTOM_ELEM) {
      return {
        ...column,
        customColHeaderRender: (props) => <CustomHeaderCellView {...props} />,
      };
    }
    if (column.headerType === headerTypes.DELETE) {
      return {
        ...column,
        headerEventHandler: deleteAllFiles,
        customColHeaderRender: (toggleDisplay) => (
          <CustomHeaderCellView openDialogBox={toggleDisplay} {...column} />
        ),
      };
    }
    /*
    * props deleteAllFiles
    */
    if (column.headerType === headerTypes.DELETE) {
      return {
        ...column,
        headerEventHandler: deleteAllFiles,
      };
    }
    return column;
  });
  return displayCustomHeader;
};
